import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

class ProductVolumeDiscountComponent extends Component {
  #abortController = new AbortController();
  #variants = [];
  #mixVariantsExpanded = false;

  #isDevelopment() {
    return Boolean(window.Shopify?.designMode) || window.location.hostname === 'localhost';
  }

  #debugLog(...args) {
    if (!this.#isDevelopment()) return;
    console.log('[Luminomo volume discount]', ...args);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#debugLog('script loaded');
    this.#parseVariants();
    this.#bindEvents();
    this.#applyTierState();
    this.#renderPrices();
    this.#toggleAcceleratedCheckout();
    this.#debugLog('block rendered', {
      productId: this.dataset.productId,
      defaultVariantId: this.dataset.defaultVariantId,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  buildCartRequest() {
    const quantity = this.#getCurrentQuantity();
    const discountPercent = this.#getDiscountPercentForQuantity(quantity);
    const discountCode = this.#getDiscountCodeForQuantity(quantity);
    const selectedVariantIds = this.#getSelectedVariantIds(quantity);
    const groupedItems = new Map();

    for (const variantId of selectedVariantIds) {
      const currentItem = groupedItems.get(variantId) || {
        id: Number(variantId),
        quantity: 0,
      };

      currentItem.quantity += 1;
      groupedItems.set(variantId, currentItem);
    }

    return {
      items: Array.from(groupedItems.values()),
      totalQuantity: quantity,
      discountCode,
    };
  }

  #bindEvents() {
    const { signal } = this.#abortController;
    const productForm = this.closest('product-form-component');

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    productForm?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    productForm?.addEventListener('input', this.#handleQuantityControlEvent, { signal });
    productForm?.addEventListener('change', this.#handleQuantityControlEvent, { signal });
    productForm?.addEventListener('click', this.#handleQuantityControlEvent, { signal });
  }

  #parseVariants() {
    const variantsJson = this.querySelector('[data-variants-json]');
    if (!variantsJson?.textContent) return;

    try {
      this.#variants = JSON.parse(variantsJson.textContent);
    } catch (error) {
      console.error('Failed to parse volume discount variants', error);
      this.#variants = [];
    }
  }

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-tier-input]')) {
      this.#syncQuantity(Number(target.getAttribute('data-tier-quantity') || target.getAttribute('value') || '1'));
      this.#applyTierState();
      this.#renderPrices();
      this.#syncMixVariantState();
      this.#toggleAcceleratedCheckout();
      this.#debugLog('tier changed', {
        quantity: this.#getCurrentQuantity(),
        discountPercent: this.#getDiscountPercentForQuantity(this.#getCurrentQuantity()),
      });
      return;
    }

    if (target.matches('[data-item-option-select]')) {
      const itemIndex = Number(target.getAttribute('data-item-index') || '0');
      this.#syncItemVariantSelection(itemIndex);
      this.#renderPrices();
    }
  };

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-mix-toggle]')) {
      this.#toggleMixVariants();
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.target === this || this.contains(event.target)) return;
    if (!this.closest('product-form-component')?.contains(event.target)) return;

    this.#scheduleQuantityRefresh();
    this.#debugLog('quantity override', {
      quantity: this.#getCurrentQuantity(),
      discountPercent: this.#getDiscountPercentForQuantity(this.#getCurrentQuantity()),
    });
  };

  #handleQuantityControlEvent = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const isQuantityInput = target.matches('input[name="quantity"]');
    const isQuantityButton = Boolean(target.closest('quantity-selector-component :is(.quantity-minus, .quantity-plus)'));
    if (!isQuantityInput && !isQuantityButton) return;

    this.#scheduleQuantityRefresh();
  };

  #handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
    if (!productId || productId.toString() !== this.dataset.productId) return;

    if (event.detail?.resource?.id) {
      this.#renderPrices();
      if (!this.#mixVariantsExpanded) {
        this.#setAllMixItemsToVariant(event.detail.resource.id.toString());
      }
    }
  };

  #getCurrentQuantity() {
    const quantityInput = this.#getQuantityInput();
    const quantity = Number(quantityInput?.value || '1');
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #getDiscountPercentForQuantity(quantity) {
    if (quantity >= 4) return 15;
    if (quantity >= 2) return 10;
    return 0;
  }

  #getDiscountCodeForQuantity(quantity) {
    const tierQuantity = quantity >= 4 ? 4 : quantity >= 2 ? 2 : 1;
    const tierInput = this.querySelector(`[data-tier-input][data-tier-quantity="${tierQuantity}"]`);
    return tierInput?.getAttribute('data-discount-code') || '';
  }

  #getSelectedVariantIds(quantity) {
    if (this.#shouldUseMixedVariantSelections(quantity)) {
      const selectedVariantIds = [];

      for (let itemIndex = 1; itemIndex <= quantity; itemIndex += 1) {
        const variant = this.#getSelectedVariantForItem(itemIndex) || this.#getVariant(this.#getCurrentMainVariantId());
        if (variant?.id) {
          selectedVariantIds.push(String(variant.id));
        }
      }

      return selectedVariantIds;
    }

    const defaultVariantId = this.#getCurrentMainVariantId();
    return defaultVariantId ? Array.from({ length: quantity }, () => defaultVariantId) : [];
  }

  #applyTierState() {
    const quantity = this.#getCurrentQuantity();
    const exactTierInput = this.querySelector(`[data-tier-input][data-tier-quantity="${quantity}"]`);

    this.querySelectorAll('[data-tier-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.getAttribute('data-tier-quantity') === String(quantity));
    });

    this.querySelectorAll('[data-tier-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input === exactTierInput;
      }
    });

    this.#syncMixVariantState();
  }

  #renderPrices() {
    const currentMainVariant = this.#getVariant(this.#getCurrentMainVariantId());
    const currentQuantity = this.#getCurrentQuantity();

    this.querySelectorAll('[data-tier-card]').forEach((card) => {
      const quantity = Number(card.getAttribute('data-tier-quantity') || '1');
      const discountPercent = this.#getDiscountPercentForQuantity(quantity);
      const isActiveMixedTier = quantity === currentQuantity && this.#shouldUseMixedVariantSelections(quantity);
      const selectedVariants = isActiveMixedTier
        ? this.#getSelectedVariantIds(quantity).map((variantId) => this.#getVariant(variantId)).filter(Boolean)
        : [];

      const subtotal = isActiveMixedTier
        ? selectedVariants.reduce((sum, variant) => sum + (variant?.price || 0), 0)
        : (currentMainVariant?.price || 0) * quantity;

      const compareSubtotal = isActiveMixedTier
        ? selectedVariants.reduce((sum, variant) => {
            const compareAtPrice = variant?.compare_at_price || 0;
            return sum + (compareAtPrice > (variant?.price || 0) ? compareAtPrice : variant?.price || 0);
          }, 0)
        : this.#getVariantCompareSubtotal(currentMainVariant, quantity);

      const discountedSubtotal = discountPercent > 0 ? Math.round(subtotal * ((100 - discountPercent) / 100)) : subtotal;
      const savings = Math.max(subtotal - discountedSubtotal, 0);
      const compareTotal = Math.max(compareSubtotal, subtotal);

      const price = card.querySelector('[data-tier-price]');
      const compare = card.querySelector('[data-tier-compare]');
      const savingsNode = card.querySelector('[data-tier-savings]');

      if (price) {
        price.textContent = this.#formatMoney(discountedSubtotal);
      }

      if (compare) {
        compare.textContent = this.#formatMoney(compareTotal);
        compare.hidden = compareTotal <= discountedSubtotal;
      }

      if (savingsNode) {
        savingsNode.textContent = savings > 0 ? `Save ${this.#formatMoney(savings)}` : '';
        savingsNode.hidden = savings === 0;
      }
    });

    this.#renderCustomPricing();
  }

  #renderCustomPricing() {
    const customPricing = this.querySelector('[data-custom-pricing]');
    if (!(customPricing instanceof HTMLElement)) return;

    const quantity = this.#getCurrentQuantity();
    const hasExactTier = Boolean(this.querySelector(`[data-tier-input][data-tier-quantity="${quantity}"]`));
    const discountPercent = this.#getDiscountPercentForQuantity(quantity);
    const currentMainVariant = this.#getVariant(this.#getCurrentMainVariantId());
    const subtotal = (currentMainVariant?.price || 0) * quantity;
    const discountedSubtotal = discountPercent > 0 ? Math.round(subtotal * ((100 - discountPercent) / 100)) : subtotal;
    const savings = Math.max(subtotal - discountedSubtotal, 0);
    const discountedUnitPrice = quantity > 0 ? Math.round(discountedSubtotal / quantity) : discountedSubtotal;
    const itemLabel = quantity === 1 ? 'item' : 'items';

    customPricing.hidden = hasExactTier || discountPercent === 0;
    if (customPricing.hidden) return;

    if (subtotal === 0) {
      this.#setText('[data-custom-total]', `You're saving ${discountPercent}%`);
      this.#setText('[data-custom-was]', '');
      this.#setText('[data-custom-save]', '');
      this.#setText('[data-custom-each]', '');
      return;
    }

    this.#setText('[data-custom-total]', `${quantity} ${itemLabel} — ${this.#formatMoney(discountedSubtotal)}`);
    this.#setText('[data-custom-was]', `Was ${this.#formatMoney(subtotal)}`);
    this.#setText('[data-custom-save]', `You save ${this.#formatMoney(savings)}`);
    this.#setText('[data-custom-each]', `${this.#formatMoney(discountedUnitPrice)} each (${discountPercent}% off)`);
  }

  #syncQuantity(quantity) {
    const quantityInput = this.#getQuantityInput();
    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.value = quantity.toString();
    const quantitySelector = quantityInput.closest('quantity-selector-component');
    quantitySelector?.updateButtonStates?.();

    this.#debugLog('quantity updated', {
      quantity,
      variantId: this.#getCurrentMainVariantId(),
    });
    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
  }

  #scheduleQuantityRefresh() {
    window.requestAnimationFrame(() => {
      this.#applyTierState();
      this.#renderPrices();
      this.#syncMixVariantState();
    });
  }

  #toggleMixVariants() {
    if (!this.#isMixVariantQuantity(this.#getCurrentQuantity())) return;

    this.#mixVariantsExpanded = !this.#mixVariantsExpanded;
    if (this.#mixVariantsExpanded) {
      this.#setAllMixItemsToVariant(this.#getCurrentMainVariantId());
    }

    this.#syncMixVariantState();
  }

  #syncMixVariantState() {
    const quantity = this.#getCurrentQuantity();
    const canMixVariants = this.dataset.canMixVariants === 'true';
    const canShowMixControl = canMixVariants && this.#isMixVariantQuantity(quantity);
    const mixContainer = this.querySelector('[data-mix-container]');
    const mixPanel = this.querySelector('[data-mix-panel]');
    const mixToggle = this.querySelector('[data-mix-toggle]');
    const mixHelper = this.querySelector('[data-mix-helper]');

    if (!canShowMixControl) {
      this.#mixVariantsExpanded = false;
    }

    if (mixContainer instanceof HTMLElement) {
      mixContainer.hidden = !canShowMixControl;
    }

    if (mixHelper instanceof HTMLElement) {
      mixHelper.hidden = !canMixVariants || quantity <= 4;
    }

    if (mixToggle instanceof HTMLButtonElement) {
      mixToggle.textContent = this.#mixVariantsExpanded
        ? 'Use same option for all items'
        : 'Choose different options for each item';
      mixToggle.setAttribute('aria-expanded', this.#mixVariantsExpanded ? 'true' : 'false');
    }

    if (mixPanel instanceof HTMLElement) {
      mixPanel.hidden = !canShowMixControl || !this.#mixVariantsExpanded;
    }

    this.querySelectorAll('[data-mix-item]').forEach((item) => {
      const itemIndex = Number(item.getAttribute('data-item-index') || '0');
      if (item instanceof HTMLElement) {
        item.hidden = !canShowMixControl || !this.#mixVariantsExpanded || itemIndex > quantity;
      }
    });

    if (canShowMixControl && this.#mixVariantsExpanded) {
      for (let itemIndex = 1; itemIndex <= quantity; itemIndex += 1) {
        this.#syncItemVariantSelection(itemIndex);
      }
    }
  }

  #isMixVariantQuantity(quantity) {
    return quantity >= 2 && quantity <= 4;
  }

  #shouldUseMixedVariantSelections(quantity) {
    return this.#mixVariantsExpanded && this.#isMixVariantQuantity(quantity) && this.dataset.canMixVariants === 'true';
  }

  #setAllMixItemsToVariant(variantId) {
    const variant = this.#getVariant(variantId);
    if (!variant || !Array.isArray(variant.options)) return;

    this.querySelectorAll('[data-item-option-select]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const optionIndex = Number(select.getAttribute('data-option-index') || '0');
      const optionValue = variant.options[optionIndex];
      if (optionValue) {
        select.value = optionValue;
      }
    });
  }

  #syncItemVariantSelection(itemIndex) {
    if (!itemIndex) return;

    const selectedOptions = this.#getSelectedOptionsForItem(itemIndex);
    let selectedVariant = this.#findVariantByOptions(selectedOptions);

    if (!selectedVariant?.available) {
      selectedVariant = this.#findBestAvailableVariant(selectedOptions);
      if (selectedVariant) {
        this.#setItemSelectValues(itemIndex, selectedVariant.options);
      }
    }

    this.#updateItemOptionAvailability(itemIndex);
  }

  #getSelectedVariantForItem(itemIndex) {
    const selectedOptions = this.#getSelectedOptionsForItem(itemIndex);
    const selectedVariant = this.#findVariantByOptions(selectedOptions);
    return selectedVariant?.available ? selectedVariant : this.#findBestAvailableVariant(selectedOptions);
  }

  #getSelectedOptionsForItem(itemIndex) {
    return Array.from(this.querySelectorAll(`[data-item-option-select][data-item-index="${itemIndex}"]`))
      .filter((select) => select instanceof HTMLSelectElement)
      .sort((a, b) => Number(a.getAttribute('data-option-index') || '0') - Number(b.getAttribute('data-option-index') || '0'))
      .map((select) => select.value);
  }

  #findVariantByOptions(options) {
    return this.#variants.find((variant) => this.#optionsMatch(variant.options, options));
  }

  #findBestAvailableVariant(preferredOptions) {
    return this.#variants.find((variant) => {
      if (!variant.available || !Array.isArray(variant.options)) return false;

      return preferredOptions.every((optionValue, optionIndex) => {
        return !optionValue || variant.options[optionIndex] === optionValue;
      });
    }) || this.#variants.find((variant) => variant.available);
  }

  #setItemSelectValues(itemIndex, options) {
    if (!Array.isArray(options)) return;

    this.querySelectorAll(`[data-item-option-select][data-item-index="${itemIndex}"]`).forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const optionIndex = Number(select.getAttribute('data-option-index') || '0');
      if (options[optionIndex]) {
        select.value = options[optionIndex];
      }
    });
  }

  #updateItemOptionAvailability(itemIndex) {
    const selectedOptions = this.#getSelectedOptionsForItem(itemIndex);

    this.querySelectorAll(`[data-item-option-select][data-item-index="${itemIndex}"]`).forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      const optionIndex = Number(select.getAttribute('data-option-index') || '0');

      Array.from(select.options).forEach((option) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = option.value;
        const hasAvailableVariant = this.#variants.some((variant) => {
          if (!variant.available || !Array.isArray(variant.options)) return false;

          return candidateOptions.every((optionValue, index) => {
            return !optionValue || variant.options[index] === optionValue;
          });
        });

        option.disabled = !hasAvailableVariant;
      });
    });
  }

  #optionsMatch(variantOptions, selectedOptions) {
    if (!Array.isArray(variantOptions) || variantOptions.length !== selectedOptions.length) return false;
    return variantOptions.every((optionValue, index) => optionValue === selectedOptions[index]);
  }

  #toggleAcceleratedCheckout() {
    const acceleratedCheckout = this.closest('product-form-component')?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = true;
    }
  }

  #getCurrentMainVariantId() {
    const variantIdInput = this.closest('product-form-component')?.querySelector('input[name="id"]');
    return variantIdInput instanceof HTMLInputElement ? variantIdInput.value : this.dataset.defaultVariantId || '';
  }

  #getQuantityInput() {
    return this.closest('product-form-component')?.querySelector('input[name="quantity"]');
  }

  #getVariant(variantId) {
    return this.#variants.find((variant) => String(variant.id) === String(variantId));
  }

  #getVariantCompareSubtotal(variant, quantity) {
    const compareAtPrice = variant?.compare_at_price || 0;
    const baseComparePrice = compareAtPrice > (variant?.price || 0) ? compareAtPrice : variant?.price || 0;
    return baseComparePrice * quantity;
  }

  #setText(selector, text) {
    const node = this.querySelector(selector);
    if (node instanceof HTMLElement) {
      node.textContent = text;
    }
  }

  #formatMoney(cents) {
    const currencyCode = this.dataset.currencyCode || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('product-volume-discount-component')) {
  customElements.define('product-volume-discount-component', ProductVolumeDiscountComponent);
}
